import { AdminPortal } from '@frontegg/nextjs';
import { AppShell } from '@mantine/core';
import {
  IconFileDescription,
  IconHelp,
  IconLogout,
  IconMenu2,
  IconUserCog,
  IconX,
} from '@tabler/icons-react';
import { getFullName } from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Avatar, getInitials } from '@zorro/design-system/v2/Avatar';
import { Button } from '@zorro/design-system/v2/Button';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { SidebarItem } from '@zorro/design-system/v2/SidebarItem';
import { ZorroLogo } from '@zorro/shared/assets';
import { Center, Group, Icon, Stack, Text } from '@zorro/web/design-system';
import { useImpersonation, useMonolithQuery } from '@zorro/web/ui-utils';
import { ReactElement, ReactNode, useState } from 'react';

import { useAnalytics } from '../../Analytics';
import { EmployerLogo } from '../../EmployerLogo';
import { EnrollmentInstructionsViewer } from '../../EnrollmentInstructions';
import { showIntercom } from '../../IntercomWidget/intercom-service';
import {
  AppLayoutConstants,
  AppLayoutHeaderSizes,
  logout,
} from '../AppLayoutUtils';
import { AppLayoutHeaderImpersonation } from './AppLayoutHeaderImpersonation';
import { AppLayoutMenuPage } from './AppLayoutMenuPage';
import { AppLayoutSubHeader } from './AppLayoutSubHeader';
import { ExitShoppingFlowModal } from './ExitShoppingFlowModal';

enum MenuView {
  MENU = 'MENU',
  INSTRUCTIONS = 'INSTRUCTIONS',
  WAIVE = 'WAIVE',
}

export type DiscoveryShoppingProps = {
  waive?: {
    onOpen: () => void;
    renderPage: (navigation: {
      onBack: () => void;
      onClose: () => void;
    }) => ReactNode;
  };
  rightElement?: ReactElement;
};

type Props = {
  openExitModal: () => void;
  isExitModalOpen: boolean;
  onContinue: () => void;
  onGoToDashboard: () => void;
  subHeaderContent?: ReactNode;
  analyticsPayload?: object;
  discoveryShoppingProps?: DiscoveryShoppingProps;
};

// This component needs to be refactored outside of AppLayout, the issue is AppLayout defines the header inside of it rather than per page
// If each page would declare it's own Header this hack wouldn't need to exist.
// https://linear.app/myzorro/issue/ENG-914/applayoutheader-refactor
export const AppLayoutHeaderDiscoveryShopping = ({
  isExitModalOpen,
  openExitModal,
  onContinue,
  onGoToDashboard,
  subHeaderContent,
  analyticsPayload,
  discoveryShoppingProps,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { isImpersonated, user } = useImpersonation();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [menuView, setMenuView] = useState(MenuView.MENU);

  const { waive, rightElement } = discoveryShoppingProps ?? {};
  const { data: employerPublicInfo } = useMonolithQuery({
    method: 'employersControllerFindEmployerPublicInfo',
    params: user && [user.tenantId],
  });

  const { data: enrollmentInstructions } = useMonolithQuery({
    method: 'enrollmentInstructionsControllerFind',
    params: user && [user.tenantId],
  });

  const handleInstructionsClick = async () => {
    setMenuView(MenuView.INSTRUCTIONS);
    await sendEvent('click_instructions_button', analyticsPayload);
  };

  const sendCloseInstructionsEvent = (
    clickSource: 'CLOSE_BUTTON' | 'CLOSE_DRAWER' | 'BACK_BUTTON'
  ) =>
    sendEvent('close_instructions', {
      ...analyticsPayload,
      click_source: clickSource,
    });

  const handleCloseMenu = async (
    clickSource: 'CLOSE_BUTTON' | 'CLOSE_DRAWER'
  ) => {
    setIsMenuOpen(false);
    if (menuView === MenuView.INSTRUCTIONS) {
      await sendCloseInstructionsEvent(clickSource);
    }
  };

  const handleInstructionsBack = async () => {
    setMenuView(MenuView.MENU);
    await sendCloseInstructionsEvent('BACK_BUTTON');
  };

  const handleWaiveClick = () => {
    setMenuView(MenuView.WAIVE);
    waive?.onOpen();
  };

  const handleHelpClick = async () => {
    await showIntercom();
    await sendEvent('click_help_button', analyticsPayload);
  };

  const handleLogoClick = async () => {
    await sendEvent('zorro_logo_click_shopping', analyticsPayload);
    openExitModal();
  };

  let impersonatedFullName: string | undefined = undefined;
  if (isImpersonated) {
    if (!user) {
      return null;
    }

    impersonatedFullName = getFullName(user);
  }

  return (
    <>
      <AppShell.Header
        bg="transparent"
        zIndex={AppLayoutConstants.HEADER_ZINDEX}
        mih="fit-content"
      >
        {impersonatedFullName && (
          <AppLayoutHeaderImpersonation fullName={impersonatedFullName} />
        )}
        <Group
          py={AppLayoutHeaderSizes.discoveryShopping.paddingBlock}
          px={{ base: 'md', xs: 'sm' }}
          gap="0"
          wrap="nowrap"
        >
          <Center
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
            h={AppLayoutHeaderSizes.discoveryShopping.logoHeight}
            mr="auto"
          >
            <ZorroLogo />
          </Center>

          {rightElement}
          <ActionIcon
            size="lg"
            aria-label="Open menu"
            onClick={() => setIsMenuOpen(true)}
          >
            <Icon icon={IconMenu2} />
          </ActionIcon>
        </Group>
        {subHeaderContent && (
          <AppLayoutSubHeader>{subHeaderContent}</AppLayoutSubHeader>
        )}
      </AppShell.Header>
      <ExitShoppingFlowModal
        isOpen={isExitModalOpen}
        onContinue={onContinue}
        onGoToDashboard={onGoToDashboard}
      />
      <Drawer
        onClose={() => handleCloseMenu('CLOSE_DRAWER')}
        onExitTransitionEnd={() => setMenuView(MenuView.MENU)}
        withCloseButton={false}
        opened={isMenuOpen}
        withFooterShadow={menuView !== MenuView.WAIVE}
        footer={
          menuView === MenuView.MENU && waive ? (
            <Group justify="space-between" p="var(--space-12)">
              <Text c="var(--color-text-default-secondary)">
                Don't need coverage?
              </Text>
              <Button variant="secondary" onClick={handleWaiveClick}>
                Waive
              </Button>
            </Group>
          ) : undefined
        }
        styles={{
          body: {
            padding: 0,
            paddingInline: 'var(--space-24)',
            paddingTop: 'var(--space-16)',
          },
        }}
      >
        {menuView === MenuView.WAIVE &&
          waive?.renderPage({
            onBack: () => setMenuView(MenuView.MENU),
            onClose: () => handleCloseMenu('CLOSE_DRAWER'),
          })}
        {menuView === MenuView.INSTRUCTIONS && (
          <AppLayoutMenuPage
            title="Enrollment instructions"
            onBack={handleInstructionsBack}
            onClose={() => handleCloseMenu('CLOSE_DRAWER')}
            footer={
              <Button
                onClick={() => handleCloseMenu('CLOSE_BUTTON')}
                variant="primary"
                w="100%"
              >
                Close
              </Button>
            }
          >
            <EnrollmentInstructionsViewer
              content={enrollmentInstructions?.content}
            />
          </AppLayoutMenuPage>
        )}
        {menuView === MenuView.MENU && (
          <>
            <Group gap="sm" wrap="nowrap">
              {employerPublicInfo?.logoUrl && (
                <EmployerLogo logoUrl={employerPublicInfo.logoUrl} />
              )}
              {user && (
                <Avatar src={user.profilePictureUrl}>
                  {getInitials(user.firstName, user.lastName)}
                </Avatar>
              )}
              <ActionIcon ml="auto" onClick={() => setIsMenuOpen(false)}>
                <Icon icon={IconX} />
              </ActionIcon>
            </Group>
            <Divider mx="-1.5rem" my="var(--space-16)" />
            <Stack gap="0">
              {enrollmentInstructions && (
                <SidebarItem
                  label="Instructions"
                  onClick={handleInstructionsClick}
                  leftSection={<Icon icon={IconFileDescription} />}
                />
              )}
              <SidebarItem
                label="My settings"
                onClick={() => AdminPortal.show()}
                leftSection={<Icon icon={IconUserCog} />}
              />
              <SidebarItem
                label="Help"
                onClick={handleHelpClick}
                leftSection={<Icon icon={IconHelp} />}
              />
              <SidebarItem
                label="Log out"
                onClick={logout}
                leftSection={<Icon icon={IconLogout} />}
              />
            </Stack>
          </>
        )}
      </Drawer>
    </>
  );
};
